import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Users, HelpCircle } from 'lucide-react';
import { AuthService } from '../services/auth';
import inquiryService from '../services/inquiryService';

const ContactForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  
  useEffect(() => {
    // Check if user is logged in
    const checkAuthState = async () => {
      try {
        const authService = new AuthService();
        await authService.checkAuthStatus();
        
        // Subscribe to auth status changes
        const subscription = authService.authStatus$.subscribe(isAuthenticated => {
          setIsLoggedIn(isAuthenticated);
          if (isAuthenticated) {
            // Get current user details
            getCurrentUserInfo(authService);
          } else {
            setUserId(null);
          }
        });
        
        // Cleanup subscription
        return () => subscription.unsubscribe();
      } catch (error) {
        setIsLoggedIn(false);
        setUserId(null);
      }
    };
    
    // Helper function to get user info
    const getCurrentUserInfo = async (authService) => {
      try {
        const attributes = await authService.fetchUserAttributes();
        // Usually the user ID is available in sub attribute
        setUserId(attributes.sub || attributes.email);
      } catch (error) {
        console.error('Error fetching user attributes:', error);
      }
    };
    
    checkAuthState();
  }, []);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for API
      const inquiryData = {
        ...formData,
        authenticated: isLoggedIn,
        userId: isLoggedIn ? userId : null,
        timestamp: new Date().toISOString()
      };
      
      // Send to backend
      await inquiryService.submitInquiry(inquiryData);
      
      setFormData({
        name: '',
        email: '',
        subject: 'general',
        message: ''
      });
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setErrors({
        submit: 'There was an error submitting your inquiry. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const subjects = [
    { value: 'general', label: 'General Inquiry', icon: HelpCircle },
    { value: 'support', label: 'Technical Support', icon: MessageSquare },
    { value: 'billing', label: 'Billing', icon: Users },
    { value: 'enterprise', label: 'Enterprise Help', icon: Users },
    { value: 'feedback', label: 'Feature Feedback', icon: MessageSquare },
    { value: 'partnership', label: 'Partnership Opportunity', icon: Users },    
    { value: 'account_deletion', label: 'Account Deletion', icon: Users }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
          {errors.submit}
        </div>
      )}
      
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Your Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full p-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
          placeholder="John Doe"
        />
        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
      </div>
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full p-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
          placeholder="johndoe@example.com"
        />
        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
      </div>
      
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
          Subject
        </label>
        <select
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          {subjects.map((subject) => (
            <option key={subject.value} value={subject.value}>
              {subject.label}
            </option>
          ))}
        </select>
      </div>
      
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="6"
          className={`w-full p-3 border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
          placeholder="How can we help you?"
        ></textarea>
        {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
      </div>
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-black text-white py-3 px-6 rounded-md hover:bg-gray-800 transition-colors flex items-center justify-center disabled:bg-gray-400"
      >
        {isSubmitting ? (
          <>
            <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></span>
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;